// src/client/auth/AuthProvider.tsx
//
// Borrower auth provider. After a successful OTP verify, Supabase Auth creates
// the auth.users row but does NOT populate `public.profiles`. This provider
// reads the profiles row by user_id (RLS-scoped) and inserts a stub on first
// sign-in so downstream FK-bound tables (kyc_submissions, loans, credit_scores)
// can resolve the borrower.
//
// Dev testing without SMS OTP: set VITE_DEV_LOGIN=true at build time and use the
// dev-login Edge Function (creates real Supabase accounts by phone, no OTP needed).
import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@client/lib/supabase';
import type { BorrowerFixture } from '@client/lib/fixtures';
import { AuthContext, type ClientAuthContextValue } from './AuthContext';

interface ProfileRow {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string;
  nrc_number: string | null;
  kyc_status: 'pending' | 'verified' | 'manual_review' | 'rejected';
  avatar_url: string | null;
  created_at: string;
}

function mapProfileRow(row: ProfileRow | Record<string, unknown>): BorrowerFixture {
  const r = row as ProfileRow;
  return {
    id: r.id,
    user_id: r.user_id,
    full_name: r.full_name ?? '',
    phone: r.phone,
    nrc: r.nrc_number ?? '',
    kyc_status: r.kyc_status,
    avatar_url: r.avatar_url ?? null,
    created_at: r.created_at,
  };
}

async function loadProfile(userId: string, phone: string | null): Promise<BorrowerFixture | null> {
  // Direct RLS-scoped read. RLS allows the borrower to SELECT their own profile,
  // so .eq('user_id', userId) is the canonical filter. We do NOT use auth.uid()
  // here; the JWT in the supabase client already binds it.
  const sel = await supabase
    .from('profiles')
    .select('id, user_id, full_name, phone, nrc_number, kyc_status, avatar_url, created_at')
    .eq('user_id', userId)
    .maybeSingle();

  if (sel.error) {
    // Treat as no-profile so the user lands on the KYC flow rather than blocking.
    console.error('[AuthProvider] profiles select failed', sel.error);
    return null;
  }

  if (!sel.data) {
    // First sign-in for this user — no profiles row yet. Insert a stub so
    // downstream queries (kyc_submissions, loans, credit_scores) can FK to it.
    // Phone is NOT NULL on profiles; pull it from the auth user.
    if (!phone) {
      console.error('[AuthProvider] cannot insert profile without phone');
      return null;
    }
    const ins = await supabase
      .from('profiles')
      .insert({ user_id: userId, phone, kyc_status: 'pending' })
      .select('id, user_id, full_name, phone, nrc_number, kyc_status, avatar_url, created_at')
      .single();
    if (ins.error || !ins.data) {
      // Race: another tab inserted at the same time. Re-fetch.
      const refetch = await supabase
        .from('profiles')
        .select('id, user_id, full_name, phone, nrc_number, kyc_status, avatar_url, created_at')
        .eq('user_id', userId)
        .maybeSingle();
      if (refetch.error || !refetch.data) {
        console.error('[AuthProvider] profile insert + refetch failed', ins.error, refetch.error);
        return null;
      }
      return mapProfileRow(refetch.data);
    }
    return mapProfileRow(ins.data);
  }

  return mapProfileRow(sel.data);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<BorrowerFixture | null>(null);
  const [status, setStatus] = useState<ClientAuthContextValue['status']>('loading');

  // Race + lifecycle guards:
  // - callIdRef: monotonic id so a slower in-flight refresh() doesn't overwrite
  //   state written by a newer call (Supabase fires INITIAL_SESSION synchronously
  //   after subscribe, so mount-refresh and listener-refresh can race).
  // - unmountedRef: prevents setState after unmount (loadProfile's network call
  //   can resolve after the component unmounts during navigation/teardown).
  const callIdRef = useRef(0);
  const unmountedRef = useRef(false);

  const refresh = useCallback(async () => {
    const id = ++callIdRef.current;
    try {
      const { data } = await supabase.auth.getSession();
      if (id !== callIdRef.current || unmountedRef.current) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      if (data.session?.user) {
        const row = await loadProfile(data.session.user.id, data.session.user.phone ?? null);
        if (id !== callIdRef.current || unmountedRef.current) return;
        setProfile(row);
        setStatus('signed_in');
      } else {
        setProfile(null);
        setStatus('signed_out');
      }
    } catch (err) {
      if (id !== callIdRef.current || unmountedRef.current) return;
      // TODO: integrate with @client/lib/sentry once we expose a captureException helper
      // Treating an unrecoverable session-fetch failure as "not signed in" is the
      // safe default — it sends the user to login where they can retry.
      console.error('[AuthProvider] refresh failed', err);
      setSession(null);
      setUser(null);
      setProfile(null);
      setStatus('signed_out');
    }
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    void refresh();
    // refresh() is the single source of truth — the listener simply re-runs it
    // on any auth event (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, INITIAL_SESSION).
    // No inline setSession/setUser writes here — they would race with refresh()
    // and cause a brief inconsistent render (e.g. session set without profile cleared).
    const { data: sub } = supabase.auth.onAuthStateChange((_event, _s) => {
      void refresh();
    });
    return () => {
      unmountedRef.current = true;
      sub.subscription.unsubscribe();
    };
  }, [refresh]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // signOut() fires SIGNED_OUT on onAuthStateChange, which calls refresh() and
    // becomes the single source of truth for clearing state. Mark loading
    // synchronously so guards block during the brief window before the listener fires.
    if (!unmountedRef.current) setStatus('loading');
  }, []);

  const deleteAccount = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    const { error: fnError } = await supabase.functions.invoke('delete-account', {});
    if (fnError) {
      const msg = (fnError as { message?: string }).message ?? 'Could not delete account';
      return { ok: false, error: msg };
    }
    // Triggers SIGNED_OUT → refresh() clears all state → redirects to login.
    await supabase.auth.signOut();
    return { ok: true };
  }, []);

  const value: ClientAuthContextValue = { session, user, profile, status, signOut, refresh, deleteAccount };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
